/**
 * One validation session: a subject playing one level (one `module_name` in the upload API), from
 * calibration to the end. It keeps the complete raw EEG, every game and host event, the alpha epochs and
 * 30 s windows, and the block-choice score after each scored decision, then packages them as one ZIP.
 *
 * All times are epoch milliseconds on the browser clock (`Date.now()`), which the game iframe shares.
 * EEG sample times come from the source's sample clock, anchored to that same clock on arrival, so every
 * event row also carries the index of the EEG sample being acquired at that moment.
 */
import {
  BlockChoiceScore, createZip, DEFAULT_CHOICE_WEIGHTS, DEFAULT_CHOICE_WINDOW,
  type ChoiceOutcome, type ChoiceScoreSnapshot, type GameEvent, type ValidationType,
} from "@myndland/game-core";
import { ALPHA_SETTINGS, type AlphaPhase, type AlphaSession, type AlphaWindow, type CalibrationResult, type Epoch } from "../eeg/alpha.ts";
import type { DeviceInfo } from "../eeg/device.ts";
import { EEG_RATE, EEG_SITES, type EegBlock } from "../eeg/muse/types.ts";

export const SESSION_FORMAT = "myndland-validation-session/1";
/** Marker actions that are player decisions even when the game does not score them. */
const DECISION_ACTIONS = new Set(["move", "tap", "valve", "skip"]);

export interface SessionMeta {
  subjectId: number;
  sessionId: string;
  program: string;
  moduleName: string;
  moduleType: ValidationType;
  game: { id: string; name: string; version: string; level: string; levelTitle: string; gameLevel: number | null };
  lambda: number;
  device: DeviceInfo;
}

export type SessionEvent = {
  t: number;
  source: "game" | "host";
  type: string;
  action?: string;
  question?: string;
  answer?: string;
  choice?: ChoiceOutcome;
  score?: number;
  detail?: unknown;
};

type StoredBlock = { t0: number; start: number; gapBefore: number; samples: Float32Array[] };
type Decision = { index: number; t: number; action: string; question: string; answer: string; choice: ChoiceOutcome | null; score: ChoiceScoreSnapshot | null };

export type SessionPackage = { zip: Uint8Array; filename: string; summary: Record<string, unknown> };

const iso = (ms: number) => new Date(ms).toISOString();
const num = (v: number | null | undefined, digits = 6) => (v == null || !Number.isFinite(v) ? "" : String(+v.toFixed(digits)));
/** RFC 4180 field. */
const csv = (v: unknown): string => {
  const s = v == null ? "" : typeof v === "string" ? v : typeof v === "number" ? (Number.isFinite(v) ? String(v) : "") : JSON.stringify(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const row = (cells: unknown[]) => cells.map(csv).join(",");
/** "puzzle=3;step=1;cell=2,4" → { puzzle: "3", step: "1", cell: "2,4" } */
export function parseQuestion(q: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of q.split(";")) {
    const eq = part.indexOf("=");
    if (eq > 0) out[part.slice(0, eq)] = part.slice(eq + 1);
  }
  return out;
}

/** A file-name-safe session id, e.g. S-20261104-101530-k3f9. */
export function newSessionId(now = Date.now(), random = Math.random): string {
  const d = new Date(now), p = (n: number) => String(n).padStart(2, "0");
  return `S-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}-${random().toString(36).slice(2, 6).padEnd(4, "0")}`;
}

export class SessionRecorder {
  readonly meta: SessionMeta;
  readonly startedAt: number;
  readonly score: BlockChoiceScore;
  #alpha: AlphaSession;
  #blocks: StoredBlock[] = [];
  #samples = 0;
  #lostSamples = 0;
  #clockBreaks = 0;
  #events: SessionEvent[] = [];
  #epochs: { epoch: Epoch; phase: AlphaPhase }[] = [];
  #decisions: Decision[] = [];
  #gameplayStartedAt: number | null = null;
  #endedAt: number | null = null;
  #endReason = "";
  #probes = 0;

  constructor(meta: SessionMeta, alpha: AlphaSession, now = Date.now()) {
    this.meta = meta;
    this.#alpha = alpha;
    this.startedAt = now;
    this.score = new BlockChoiceScore();
    this.host("session_start", now, { subject_id: meta.subjectId, session_id: meta.sessionId, module_name: meta.moduleName, simulated: meta.device.simulated });
  }

  get ended(): boolean { return this.#endedAt !== null; }
  get samples(): number { return this.#samples; }
  get decisions(): number { return this.#decisions.length; }
  get events(): number { return this.#events.length; }
  get gameplayStartedAt(): number | null { return this.#gameplayStartedAt; }

  eeg(block: EegBlock): void {
    if (this.ended) return;
    const n = block.samples[0]?.length ?? 0;
    if (!n) return;
    if (block.gapBefore === Number.POSITIVE_INFINITY) this.#clockBreaks++;
    else if (block.gapBefore > 0) this.#lostSamples += block.gapBefore;
    this.#blocks.push({ t0: block.t0 * 1000, start: this.#samples, gapBefore: block.gapBefore, samples: EEG_SITES.map((_, c) => block.samples[c] ?? new Float32Array(n).fill(NaN)) });
    this.#samples += n;
  }

  epoch(epoch: Epoch): void {
    if (!this.ended) this.#epochs.push({ epoch, phase: this.#alpha.phase });
  }

  /** A host-side event: session boundaries, calibration, probes, signal quality and device status. */
  host(type: string, t = Date.now(), detail?: unknown, answer?: string): void {
    if (this.ended) return;
    this.#events.push({ t, source: "host", type, answer, detail });
  }

  markGameplayStart(t: number): void {
    if (this.#gameplayStartedAt === null) this.#gameplayStartedAt = t;
  }

  probe(t: number, answer: string, rtMs: number, question: string): void {
    this.#probes++;
    this.host("probe", t, { question, rt_ms: Math.round(rtMs), probe: this.#probes }, answer);
  }

  /** Every game event goes into the record; markers that are decisions also update the block-choice score. */
  game(ev: GameEvent): ChoiceScoreSnapshot | null {
    if (this.ended) return null;
    const e: SessionEvent = { t: ev.t, source: "game", type: ev.type };
    if (ev.type === "marker") Object.assign(e, { action: ev.action, question: ev.question, answer: ev.answer, choice: ev.choice, score: ev.score });
    else if (ev.type === "solved" || ev.type === "progress") e.detail = ev.detail;
    else if (ev.type === "record") Object.assign(e, { action: ev.name, detail: ev.detail });
    else if (ev.type === "log") e.detail = ev.message;
    else if (ev.type === "error") e.detail = { message: ev.message, stack: ev.stack };
    this.#events.push(e);
    if (ev.type !== "marker" || (!ev.choice && !DECISION_ACTIONS.has(ev.action))) return null;
    const snap = ev.choice ? this.score.add(ev.choice) : null;
    this.#decisions.push({ index: this.#decisions.length + 1, t: ev.t, action: ev.action, question: ev.question, answer: ev.answer, choice: ev.choice ?? null, score: snap });
    return snap;
  }

  end(reason: string, now = Date.now()): void {
    if (this.ended) return;
    this.host("session_end", now, undefined, reason);
    this.#endedAt = now;
    this.#endReason = reason;
  }

  /** Index of the EEG sample being acquired at `t` (epoch ms), or null when there was no EEG then. */
  sampleIndexAt(t: number): number | null {
    const b = this.#blocks;
    if (!b.length || t < b[0]!.t0) return null;
    let lo = 0, hi = b.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (b[mid]!.t0 <= t) lo = mid; else hi = mid - 1; }
    const blk = b[lo]!, len = blk.samples[0]!.length;
    const offset = Math.floor(((t - blk.t0) * EEG_RATE) / 1000);
    // Past this block's end by more than a second: the stream had stopped.
    if (offset >= len + EEG_RATE) return null;
    return blk.start + Math.min(len - 1, offset);
  }

  #eegCsv(): string {
    const out: string[] = [row(["sample_index", "t_unix_ms", ...EEG_SITES.map((s) => `${s}_uV`), "gap_before"])];
    const dt = 1000 / EEG_RATE;
    for (const blk of this.#blocks) {
      const len = blk.samples[0]!.length;
      for (let i = 0; i < len; i++) {
        const gap = i === 0 && blk.gapBefore !== 0 ? (Number.isFinite(blk.gapBefore) ? String(blk.gapBefore) : "break") : "";
        let line = `${blk.start + i},${(blk.t0 + i * dt).toFixed(3)}`;
        for (const ch of blk.samples) { const v = ch[i]!; line += Number.isFinite(v) ? `,${+v.toFixed(3)}` : ","; }
        out.push(`${line},${gap}`);
      }
    }
    return out.join("\n") + "\n";
  }

  #eventsCsv(): string {
    const out = [row(["event_index", "t_unix_ms", "t_iso", "eeg_sample_index", "source", "type", "action", "question", "answer", "choice", "score", "detail"])];
    [...this.#events].sort((a, b) => a.t - b.t).forEach((e, i) => out.push(row([
      i + 1, e.t, iso(e.t), this.sampleIndexAt(e.t), e.source, e.type, e.action, e.question, e.answer, e.choice, e.score, e.detail,
    ])));
    return out.join("\n") + "\n";
  }

  #decisionsCsv(): string {
    const out = [row(["decision_index", "t_unix_ms", "t_iso", "eeg_sample_index", "action", "puzzle", "step", "cell", "answer", "choice",
      "block_choice_score", "choices_included", "window_correct", "window_related", "window_irrelevant", "question"])];
    for (const d of this.#decisions) {
      const q = parseQuestion(d.question);
      out.push(row([d.index, d.t, iso(d.t), this.sampleIndexAt(d.t), d.action, q.puzzle, q.step, q.cell, d.answer, d.choice ?? "unscored",
        d.score?.score, d.score?.included, d.score?.correct, d.score?.related, d.score?.irrelevant, d.question]));
    }
    return out.join("\n") + "\n";
  }

  #epochsCsv(): string {
    const head = ["epoch_index", "t_start_unix_ms", "t_end_unix_ms", "eeg_sample_index_end", "phase", "usable", "rel_alpha"];
    for (const s of EEG_SITES) head.push(`${s}_rel_alpha`, `${s}_usable`, `${s}_reason`);
    const out = [row(head)];
    this.#epochs.forEach(({ epoch: e, phase }, i) => {
      const cells: unknown[] = [i + 1, e.tStart.toFixed(3), e.tEnd.toFixed(3), this.sampleIndexAt(e.tEnd - 1), phase, e.usable ? 1 : 0, num(e.relAlpha)];
      for (const ch of e.channels) cells.push(num(ch.relAlpha), ch.usable ? 1 : 0, ch.reason);
      out.push(row(cells));
    });
    return out.join("\n") + "\n";
  }

  #windowsCsv(windows: readonly AlphaWindow[]): string {
    const out = [row(["window_index", "t_start_unix_ms", "t_end_unix_ms", "t_start_iso", "usable_seconds", "mean_rel_alpha", "baseline_before",
      "engagement_indicator", "baseline_after", "partial"])];
    for (const w of windows) {
      out.push(row([w.index, w.tStart.toFixed(3), w.tEnd.toFixed(3), iso(w.tStart), w.usableSeconds, num(w.mean), num(w.baselineBefore),
        num(w.indicator), num(w.baselineAfter), w.partial ? 1 : 0]));
    }
    return out.join("\n") + "\n";
  }

  /** The ZIP's file name: program, module, subject and session, e.g. waterway_Stonebrook_subject101_S-20261104-101530-k3f9.zip */
  get filename(): string {
    const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
    return `${safe(this.meta.program)}_${safe(this.meta.moduleName)}_subject${this.meta.subjectId}_${safe(this.meta.sessionId)}.zip`;
  }

  /** Builds the ZIP. Call after `end()`. */
  async package(): Promise<SessionPackage> {
    if (this.#endedAt === null) throw new Error("End the session before packaging it");
    const m = this.meta;
    const windows = this.#alpha.windows;
    const calibration: CalibrationResult | null = this.#alpha.calibration;
    const finalScore = this.score.snapshot();
    const first = this.#blocks[0], last = this.#blocks[this.#blocks.length - 1];
    const files = ["session.json", "README.txt", "eeg.csv", "events.csv", "decisions.csv", "alpha_epochs.csv", "alpha_windows.csv"];
    const summary = {
      format: SESSION_FORMAT,
      subject_id: m.subjectId,
      session_id: m.sessionId,
      validation_program: m.program,
      module_name: m.moduleName,
      module_type: m.moduleType,
      game: m.game,
      started_at: iso(this.startedAt),
      gameplay_started_at: this.#gameplayStartedAt === null ? null : iso(this.#gameplayStartedAt),
      ended_at: iso(this.#endedAt),
      end_reason: this.#endReason,
      simulated: m.device.simulated,
      device: m.device,
      clock: "All t_unix_ms values are milliseconds since 1970-01-01 UTC on the recording browser's clock (Date.now), shared by the game frame. " +
        "EEG sample times count samples at the nominal 256 Hz from an arrival-time anchor that slowly follows the browser clock (SensorClock, slew 0.002); " +
        "gap_before marks lost samples or a clock break. eeg_sample_index in the other files is the EEG sample being acquired at that time.",
      eeg: {
        file: "eeg.csv", units: "µV", sites: EEG_SITES, sample_rate_hz: EEG_RATE, samples: this.#samples,
        lost_samples: this.#lostSamples, clock_breaks: this.#clockBreaks,
        first_sample_unix_ms: first ? first.t0 : null,
        last_sample_unix_ms: last ? last.t0 + ((last.samples[0]!.length - 1) * 1000) / EEG_RATE : null,
      },
      settings: {
        alpha: { ...ALPHA_SETTINGS, lambda: m.lambda },
        block_choice_score: { window: DEFAULT_CHOICE_WINDOW, weights: DEFAULT_CHOICE_WEIGHTS, formula: "100 * (correct + 0.5 * related) / scored choices in window" },
      },
      calibration: calibration && {
        ...calibration, started_at: iso(calibration.startedAt), ended_at: iso(calibration.endedAt),
      },
      original_baseline: calibration?.baseline ?? null,
      final_baseline: windows.length ? windows[windows.length - 1]!.baselineAfter : calibration?.baseline ?? null,
      windows: windows.length,
      windows_with_indicator: windows.filter((w) => w.indicator !== null).length,
      behaviour: { decisions: this.#decisions.length, scored_choices: finalScore.total, final_block_choice_score: finalScore },
      events: this.#events.length,
      probes: this.#probes,
      files,
    };
    const zip = await createZip([
      { name: "session.json", data: JSON.stringify(summary, null, 2) + "\n" },
      { name: "README.txt", data: README },
      { name: "eeg.csv", data: this.#eegCsv() },
      { name: "events.csv", data: this.#eventsCsv() },
      { name: "decisions.csv", data: this.#decisionsCsv() },
      { name: "alpha_epochs.csv", data: this.#epochsCsv() },
      { name: "alpha_windows.csv", data: this.#windowsCsv(windows) },
    ]);
    return { zip, filename: this.filename, summary };
  }
}

const README = `Myndland validation session (${SESSION_FORMAT})

session.json       Subject and session identifiers, module, device, settings, calibration result and summary.
eeg.csv            Complete raw EEG: one row per sample, 256 Hz, µV per electrode (TP9, AF7, AF8, TP10).
                   An empty value is a sample the headset did not deliver. gap_before on a row gives the number of
                   samples lost just before it, or "break" where the sample clock restarted.
events.csv         Every game and host event in time order: game markers (actions, 1 Hz focus samples,
                   calibration, solves), flow records, probes and session boundaries.
decisions.csv      One row per player decision. choice is correct, related (a mistake about the task), irrelevant
                   (a choice that cannot help), or unscored. block_choice_score is the score over the latest 10
                   scored choices after this decision: 100 * (correct + 0.5 * related) / choices included.
alpha_epochs.csv   2 s epochs every 1 s: relative alpha (8-12 Hz / 1-35 Hz) per electrode, whether it was usable
                   and why not. rel_alpha is the mean of the usable TP9/TP10 values. phase is calibrating,
                   calibrated (between calibration and play), gameplay or ended.
alpha_windows.csv  30 s gameplay windows: usable seconds, mean relative alpha, the baseline before the window, the
                   experimental engagement indicator (baseline_before - mean) / baseline_before, and the rolling
                   baseline after it: (1 - lambda) * before + lambda * mean. Windows with less than 15 s of usable
                   EEG have no indicator and keep the baseline. The original baseline is in session.json.

Times: t_unix_ms is milliseconds since 1970-01-01 UTC on the recording browser's clock. eeg_sample_index
matches a row to eeg.csv's sample_index.

The behavioural score and the EEG indicator are kept separate on purpose: both are being evaluated.
`;
